import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DayTimeline } from '@/components/planner/DayTimeline';
import { useAuth } from '@/hooks/useAuth';
import { useTrips } from '@/hooks/useTrips';
import { ArrowLeft, MapPin, Calendar, Wallet, Share2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const TripDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { tripsQuery } = useTrips(user?.id);

  if (authLoading || tripsQuery?.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const trip = tripsQuery.data?.find((t) => String(t.id) === id);

  if (!trip) {
    return (
      <div className="min-h-screen bg-bg-primary">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-32 pb-20 text-center">
          <h2 className="text-2xl font-bold text-text-primary mb-4">行程未找到</h2>
          <Button onClick={() => navigate('/profile')} className="bg-primary hover:bg-primary-hover text-white rounded-btn">
            返回个人中心
          </Button>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: trip.title, text: trip.destination });
    } else {
      toast.info('浏览器不支持分享功能');
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" onClick={() => navigate('/profile')} className="text-text-secondary hover:text-primary">
            <ArrowLeft className="w-4 h-4 mr-1" />
            返回
          </Button>
        </div>

        <Card className="border-0 shadow-card rounded-card bg-white mb-8">
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold text-text-primary mb-4">{trip.title || trip.destination}</h1>
            <div className="flex flex-wrap gap-3 mb-4">
              <Badge variant="secondary" className="bg-primary-light text-primary rounded-full">
                <MapPin className="w-3 h-3 mr-1" />
                {trip.destination}
              </Badge>
              <Badge variant="secondary" className="bg-bg-secondary text-secondary rounded-full">
                <Calendar className="w-3 h-3 mr-1" />
                {trip.days}天
              </Badge>
              {trip.budget && (
                <Badge variant="secondary" className="bg-success/10 text-success rounded-full">
                  <Wallet className="w-3 h-3 mr-1" />
                  ¥{trip.budget}
                </Badge>
              )}
              {trip.style && (
                <Badge variant="secondary" className="bg-accent/20 text-secondary rounded-full">
                  {trip.style}
                </Badge>
              )}
            </div>
            <p className="text-sm text-text-tertiary">
              创建于 {format(new Date(trip.created_at), 'yyyy年MM月dd日')}
            </p>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleShare} variant="outline" className="rounded-btn border-[#E8E4DF]">
                <Share2 className="w-4 h-4 mr-2" />
                分享
              </Button>
            </div>
          </CardContent>
        </Card>

        {trip.content?.days?.map((day, index) => (
          <DayTimeline key={index} day={day} dayNumber={index + 1} />
        ))}
      </div>
    </div>
  );
};

export default TripDetail;
